/**
 * The two columns.
 *
 * Both are on screen at all times and only the emphasis moves, because the
 * comparison IS the content. Hiding the column you are not asking about would
 * turn one question into two pages and let a reader leave with half the answer.
 *
 * The inactive column is dimmed, not disabled: its figures stay readable and
 * its footnotes stay clickable, so nothing is hidden behind an interaction.
 */

import { evaluateMetric } from '../engine/evaluate.js';
import type { Metric, Pack, Range, Scenario } from '../engine/schema.js';
import { buildScope, comparatorDivisor } from '../engine/scope.js';
import { formatValue, roundToSigFigs } from '../engine/units.js';
import type { Footnotes } from './footnotes.js';
import { esc, formatMid, num } from './num.js';
import { categoryTag, selectedOutcomes } from './outcomes.js';
import type { AppState } from './store.js';

/** One row of the comparison: a heading, a blurb, and the metrics under it. */
export interface ImpactArea {
  id: string;
  title: string;
  /** The question the row answers, in the reader's words. */
  blurb: string;
  metricIds: string[];
}

export const IMPACT_AREAS: ImpactArea[] = [
  {
    id: 'taxes',
    title: 'Taxes and schools',
    blurb:
      'Money the town and the school district would collect, after the tax break is subtracted, and what that money is the size of.',
    metricIds: ['net_local_tax', 'school_teacher_years', 'community_fund_share_of_city'],
  },
  {
    id: 'jobs',
    title: 'Work and training',
    blurb:
      'Construction work on the first phase, and the apprentices who could bank a full year of required hours on it.',
    metricIds: ['construction_job_years', 'permanent_jobs', 'construction_apprenticeships'],
  },
  {
    id: 'bills',
    title: 'Electricity and the grid',
    blurb:
      'How much the utility estimates the agreement would take off other Minnesota customers’ bills, and how much of its own power the facility brings with it.',
    metricIds: ['bill_savings_monthly', 'system_price_effect', 'capacity_coverage'],
  },
];

/**
 * Areas the reader asked about come first. Stable within each half, so an
 * unselected page keeps the authored order, and nothing is ever removed.
 */
export function orderedAreas(state: AppState): ImpactArea[] {
  const wanted = new Set(
    selectedOutcomes(state)
      .map((o) => o.anchor)
      .filter((a) => a.startsWith('area-'))
      .map((a) => a.slice('area-'.length)),
  );
  if (!wanted.size) return IMPACT_AREAS;
  return [
    ...IMPACT_AREAS.filter((a) => wanted.has(a.id)),
    ...IMPACT_AREAS.filter((a) => !wanted.has(a.id)),
  ];
}

export function comparison(
  pack: Pack,
  state: AppState,
  fn: Footnotes,
  built: Scenario,
  notBuilt: Scenario,
): string {
  return `<section class="compare" id="compare"><div class="container">
    ${columnHeads(state, built, notBuilt)}
    ${orderedAreas(state)
      .map((area) => areaRow(pack, state, fn, area, built, notBuilt))
      .join('')}
  </div></section>`;
}

function columnHeads(state: AppState, built: Scenario, notBuilt: Scenario): string {
  return `<div class="compare-heads">
    <div class="compare-spacer" aria-hidden="true"></div>
    ${[built, notBuilt]
      .map(
        (s) => `<div class="compare-head ${state.scenarioId === s.id ? 'active' : 'muted'}">
          <span class="eyebrow">${state.scenarioId === s.id ? 'You are here' : ''}</span>
          <h3 class="compare-head-title">${esc(s.label)}</h3>
          <p class="compare-head-sub">${esc(s.plainLabel)}</p>
        </div>`,
      )
      .join('')}
  </div>`;
}

function areaRow(
  pack: Pack,
  state: AppState,
  fn: Footnotes,
  area: ImpactArea,
  built: Scenario,
  notBuilt: Scenario,
): string {
  const metrics = area.metricIds
    .map((id) => pack.metrics.find((m) => m.id === id))
    .filter((m): m is Metric => Boolean(m));
  if (!metrics.length) return '';

  return `<div class="compare-row" id="area-${esc(area.id)}">
    <div class="compare-label">
      <h3 class="compare-area-title">${esc(area.title)}</h3>
      <p class="compare-area-blurb">${esc(area.blurb)}</p>
    </div>
    ${[built, notBuilt]
      .map(
        (s) => `<div class="compare-cell ${state.scenarioId === s.id ? 'active' : 'muted'}">
          ${metrics.map((m) => metricStory(pack, state, fn, m, s, built)).join('')}
        </div>`,
      )
      .join('')}
  </div>`;
}

/**
 * One metric under one scenario, told as a consequence rather than a reading.
 *
 * Under `built` the equivalents say what the money is the size of. Under
 * `not_built` the same equivalents are framed as what does not arrive, and they
 * are computed from the BUILT figure — the loss is the thing that would have
 * happened, so a column of zeroes would say nothing at all.
 */
function metricStory(
  pack: Pack,
  state: AppState,
  fn: Footnotes,
  metric: Metric,
  scenario: Scenario,
  built: Scenario,
): string {
  if (metric.appliesTo && !metric.appliesTo.includes(scenario.id)) {
    return `<div class="story">
      <p class="story-na">Does not apply. ${esc(metric.plainLabel.toLowerCase())} only means something if the facility exists.</p>
    </div>`;
  }

  const scopeState = { scenarioId: scenario.id, params: {}, overrides: state.overrides };
  const result = evaluateMetric(pack, metric, scopeState);
  const isLoss = scenario.id !== built.id;

  // The counterfactual figure: what this scenario forgoes relative to built.
  const builtResult = evaluateMetric(pack, metric, {
    scenarioId: built.id,
    params: {},
    overrides: state.overrides,
  });

  return `<div class="story">
    ${categoryTag(metric.category)}
    <p class="story-q">${esc(metric.question)}${fn.ref(metric.id)}</p>
    ${num(result, { drawerId: metric.id })}
    ${equivalents(pack, state, metric, builtResult.value as Range, scenario, isLoss)}
  </div>`;
}

function equivalents(
  pack: Pack,
  state: AppState,
  metric: Metric,
  builtValue: Range,
  scenario: Scenario,
  isLoss: boolean,
): string {
  if (!metric.comparatorIds?.length) return '';
  const subject = builtValue?.mid;
  if (!subject) return '';

  const scope = buildScope(pack, {
    scenarioId: scenario.id,
    params: {},
    overrides: state.overrides,
  });

  const lines: string[] = [];
  for (const id of metric.comparatorIds) {
    const c = pack.comparators.find((x) => x.id === id);
    if (!c) continue;
    const divisor = comparatorDivisor(c, scope);
    if (divisor === undefined) continue;

    const multiple = subject / divisor;
    // Two significant figures. A comparison carrying more precision than that
    // is claiming an accuracy the underlying estimate does not have.
    const shown = formatValue(roundToSigFigs(multiple, 2), 'count');

    // The verb only earns its place as a connector. Every other plainLabel in
    // the pack already leads with its own noun ("acres of cropland…", "years of
    // running…"), so prefixing the verb would stutter.
    lines.push(
      `<li><span class="eq-n mono">${esc(shown)}</span> <span class="eq-t">${
        c.verb === 'times' ? 'times ' : ''
      }${esc(c.plainLabel)}</span><span class="eq-basis" title="${esc(
        basisNote(c.basis),
      )}">${esc(basisLabel(c.basis))}</span></li>`,
    );
  }

  if (!lines.length) return '';

  return `<div class="equivalents">
    <p class="eq-lead">${
      isLoss
        ? 'What Minnesota does not get, in things it already buys:'
        : 'The same money, in things the town already buys:'
    }</p>
    <ul class="eq-list">${lines.join('')}</ul>
  </div>`;
}

/**
 * Basis labels are not decoration. Three of the water comparisons are measured
 * actuals and the facility figure is a permitted ceiling; presenting a multiple
 * without saying which is the classic error in this argument, and the pack's
 * own caveats call it out.
 */
function basisLabel(basis: string): string {
  switch (basis) {
    case 'permitted-maximum':
      return 'permitted max';
    case 'measured-actual':
      return 'measured';
    case 'budgeted':
      return 'budgeted';
    default:
      return 'modelled';
  }
}

function basisNote(basis: string): string {
  switch (basis) {
    case 'permitted-maximum':
      return 'A permitted ceiling, not a measured withdrawal.';
    case 'measured-actual':
      return 'What was actually used or spent, not a permitted ceiling.';
    case 'budgeted':
      return 'An adopted budget figure.';
    default:
      return 'Modelled rather than measured.';
  }
}

/** Plain text of a metric's headline figure, for the assumptions section. */
export function headlineText(pack: Pack, state: AppState, metric: Metric, scenarioId: string): string {
  const r = evaluateMetric(pack, metric, { scenarioId, params: {}, overrides: state.overrides });
  if (r.blocked) return 'not publicly disclosed';
  return formatMid(r.value as Range, r.unit, r.sigFigs);
}
